import React, { useRef, useState } from 'react';
import {
    BaseOperateView,
    BaseStagePlugin,
    getFramework,
    getGroupExtension,
    getOperateType,
    useActiveOriginIndex,
    useIdentity,
    useMoveEventListener,
} from '@/framework';
import { useLocaleStore } from '@/i18n/index';
import { ITouchKitEvent } from '@/utils/event-kit';
import { translatePointAngle } from '@/utils/math-kit';
import { traverseTrees } from '@/utils/tree-kit';
import {
    GroupRoleType,
    IExportPlugin,
    IGroupLayerProps,
    IImageLayerProps,
    ILayerProps,
    IPoint,
    IPuzzleLayer,
    LayerType,
    PluginType,
} from '@/version-types';
import './drop-img-plugin.less';
import classNames from 'classnames';
import cloneDeep from 'lodash/cloneDeep';
import { IFramework } from '@/framework/instance';
import ImageEffectAction from '@/layers/image-layer/utils/image-effect-action';
import { doImageEffect, replaceImage } from '@/layers/image-layer/utils/layer';
import { getLayerViewNode } from '@/utils/layer-kit';
import { DropImageHistory, HistoryType } from './drop-img-history';

const pointInLayer = (point: IPoint, centerPoint: IPoint, width: number, height: number) => {
    // 计算长方形的边界
    const left = centerPoint.x - width / 2;
    const right = centerPoint.x + width / 2;
    const top = centerPoint.y + height / 2;
    const bottom = centerPoint.y - height / 2;

    // 判断点是否在长方形内
    return point.x >= left && point.x <= right && point.y <= top && point.y >= bottom;
};

const checkCursorPointInRect = (layer: ILayerProps, point: IPoint, framework: IFramework) => {
    const elem = getLayerViewNode(framework.identity, layer.id);
    if (!elem) return false;

    const { left, top, width: elemWidth, height: elemHeight } = elem.getBoundingClientRect();
    const centerPoint = {
        x: left + elemWidth / 2,
        y: top + elemHeight / 2,
    };
    const rotatePoint = {
        x: point.x - centerPoint.x,
        y: (point.y - centerPoint.y) * -1,
    };

    return pointInLayer(rotatePoint, { x: 0, y: 0 }, 84, 40);
};

/**
 * 检查鼠标是否在图片图层上
 *
 * @param layer - 图片图层
 * @param point - 鼠标位置
 * @returns 是否在图片图层上
 */
const checkCursorPointInImageLayer = (layer: ILayerProps, point: IPoint, scale: number, framework: IFramework) => {
    const elem = getLayerViewNode(framework.identity, layer.id);
    if (!elem) return false;
    const { width, height, rotate } = layer;
    const { left, top, width: elemWidth, height: elemHeight } = elem.getBoundingClientRect();
    const centerPoint = {
        x: left + elemWidth / 2,
        y: top + elemHeight / 2,
    };
    const layerWidth = width * scale;
    const layerHeight = height * scale;
    const rotatePoint = translatePointAngle(
        {
            x: point.x - centerPoint.x,
            y: (point.y - centerPoint.y) * -1,
        },
        -rotate,
    );

    return pointInLayer(rotatePoint, { x: 0, y: 0 }, layerWidth, layerHeight);
};

const DropImgPluginView = React.memo(() => {
    const { $t } = useLocaleStore();
    const identity = useIdentity();
    const framework = getFramework(identity);
    const activeOriginIndex = useActiveOriginIndex();
    const [targetLayer, setTargetLayer] = useState<ILayerProps | null>(null);
    const [canDrop, setCanDrop] = useState(false);
    const originLayer = useRef<ILayerProps | null>(null);

    const checkIsPuzzle = (layer: ILayerProps) => {
        const parentId = layer.parentId;
        if (!parentId) return false;
        const parentLayer = framework.getLayer(parentId);

        if (!parentLayer) return false;
        return (
            parentLayer.layerType === LayerType.GROUP_LAYER &&
            (parentLayer.data as IGroupLayerProps).extension?.role === GroupRoleType.PUZZLE
        );
    };

    useMoveEventListener((e: ITouchKitEvent) => {
        const target = e.target;
        const dataType = target.getAttribute('data-operate-type');
        const dataLayerType = target.getAttribute('data-layer-type');

        if (!getOperateType(target) && dataLayerType !== 'img') return;

        if (dataType !== 'move' && dataLayerType !== 'img') return;
        const activeLayer = framework.operate.getActiveLayer();

        if (!activeLayer || activeLayer.layerType !== LayerType.IMAGE_LAYER) {
            return;
        }

        if (e.isStart) {
            originLayer.current = activeLayer;
        }

        const layers = framework.stage.getActiveStageLayers();
        const allImageLayers = [] as ILayerProps[];
        traverseTrees(layers, (layer) => {
            if (
                layer.layerType === LayerType.IMAGE_LAYER &&
                layer.id !== activeLayer.id &&
                ((layer.data as IImageLayerProps).maskURL || checkIsPuzzle(layer))
            ) {
                allImageLayers.push(layer);
            }
        });

        if (allImageLayers.length === 0) {
            return;
        }

        const elem = getLayerViewNode(framework.identity, activeLayer.id);
        if (!elem) {
            return;
        }
        const scale = framework.stage.getScale();
        let canDrop = false;
        for (let i = 0; i < allImageLayers.length; i++) {
            const layer = allImageLayers[i];
            if (checkCursorPointInImageLayer(layer, e.currentPoint, scale, framework)) {
                (elem as HTMLDivElement).style.opacity = '0.3';
                setTargetLayer(layer);

                if (checkCursorPointInRect(layer, e.currentPoint, framework)) {
                    setCanDrop(true);
                    canDrop = true;
                } else {
                    setCanDrop(false);
                    canDrop = false;
                }
                break;
            } else {
                setTargetLayer(null);
                (elem as HTMLDivElement).style.opacity = '1';
            }
        }

        if (e.isEnd) {
            setTargetLayer(null);
            (elem as HTMLDivElement).style.opacity = '1';
            setCanDrop(false);

            if (!canDrop || !targetLayer || !activeLayer) {
                return;
            }
            const dropLayer = cloneDeep(activeLayer);
            const oldTargetLayer = cloneDeep(targetLayer);
            if (!checkIsPuzzle(oldTargetLayer)) framework.operate.setActiveLayer(oldTargetLayer.id);

            const dropIn = async () => {
                const newLayer = await replaceImage(
                    oldTargetLayer,
                    (dropLayer.data as IImageLayerProps).url,
                    framework.stage.getViewWidth(),
                    dropLayer.prompt || '',
                    dropLayer.materialIds || {},
                    (dropLayer.data as IImageLayerProps).naturalWidth,
                    (dropLayer.data as IImageLayerProps).naturalHeight,
                );

                framework.operate.delLayer(dropLayer.id);
                framework.stage.addOrUpdate(newLayer);
                if (!ImageEffectAction.isEmptyEffect(newLayer.data as IImageLayerProps)) {
                    await doImageEffect(identity, newLayer.id);
                }
                framework.operate.setActiveLayer(newLayer.id);
                framework.history.write({
                    type: HistoryType.DROP_IMAGE,
                    data: {
                        item: {
                            fromLayer: cloneDeep(originLayer.current),
                            oldTargetLayer,
                            newTargetLayer: newLayer,
                            layerIndex: framework.tryGetLayerOriginIndex(targetLayer.id),
                        },
                    },
                });
            };

            dropIn();
        }
    });

    if (!targetLayer) {
        return null;
    }

    return (
        <BaseStagePlugin originIndexOrLayerId={activeOriginIndex} name="drop-img">
            <div className="l-render-drop-image">
                <BaseOperateView layer={targetLayer}>
                    <div
                        className={classNames({
                            'l-render-drop-image__btn': true,
                            'drag-in': !canDrop,
                            release: canDrop,
                        })}
                        style={{
                            transform: ` translate(-50%, -50%)) rotate(-${targetLayer.rotate}deg)`,
                        }}
                    >
                        {canDrop
                            ? $t((v) => v.imageLayer.renderDropImage.freedImage)
                            : $t((v) => v.imageLayer.renderDropImage.dropImage)}
                    </div>
                </BaseOperateView>
            </div>
        </BaseStagePlugin>
    );
});

const exportPlugin: IExportPlugin = {
    type: PluginType.STAGE,
    sort: 3,
    name: 'drop-img',
    render() {
        return <DropImgPluginView />;
    },
    createHistory({ identity }) {
        return [new DropImageHistory(identity)];
    },
};

export default exportPlugin;
